import { Component, OnInit } from '@angular/core';
import { Company } from "../../model/company";
import { Constants } from "../../utils/constants";
import { AuthService } from "../../services/auth.service";
import { UsersService } from "../../services/users.service";
import { Contract } from "../../model/contract";
import { ContractsService } from "../../services/contracts.service";
import { BoxesService } from "../../services/boxes-service";
import { Testbed } from "../../model/testbed";
import { Box } from "../../model/box";
import { forkJoin, Observable, of } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { BoxAndTestbed } from '../../model/box-and-testbed';
import { TestbedsService } from 'src/app/services/testbeds.service';

@Component({
  selector: 'aw-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {

  loggedUser: any;
  company?: Company;
  roles = Constants.roles;
  contractStatuses = Constants.contractStatuses;
  boxStatuses = Constants.boxStatuses;
  entities = Constants.entities;
  contracts?: Contract[] = [];
  boxes?: Box[] = [];
  testbeds: Testbed[] = [];
  testbedsIds: number[] = [];
  certificates?: [] = [];
  loadingCompany = false;
  loadingContracts = false;
  loadingBoxes = false;
  loadingBoxesAndTestbeds = false;
  isThereAnyContract = false;
  isThereAnyBox = false;
  isThereAnyCertificate = false;
  isThereAnyTestbed = false;
  private allTheStuff$!: Observable<any>;
  boxesWithTestbed?: BoxAndTestbed[];

  constructor(private authService: AuthService,
    private usersService: UsersService,
    private contractsService: ContractsService,
    private boxesService: BoxesService,
    private testbedsService: TestbedsService,
  ) {
    this.loggedUser = this.authService.loggedUser();
    this.company = this.loggedUser?.company as Company;
  }

  ngOnInit(): void {

    switch (this.loggedUser.role) {

      case this.roles.admin:
        this.allTheStuff$ =
          of(true)
            .pipe(
              tap(() => {
                this.loadingContracts = true;
                this.loadingBoxes = true;
              }),
              switchMap(() => this.contractsService.getAllContracts([this.contractStatuses.new, this.contractStatuses.approvalWaiting, this.contractStatuses.ongoing])),
              tap(contracts => {
                this.contracts = contracts;
                this.loadingContracts = false;
                this.loadItems();
              }),
              switchMap(() => this.boxesService.getAllBoxes([this.boxStatuses.delivered, this.boxStatuses.toShipBack])),
              tap(boxes => {
                this.boxes = boxes;
                this.loadingBoxes = false;
                this.loadItems();
              }),
            );
        this.allTheStuff$.subscribe();
        break;

      case this.roles.customer:
        this.allTheStuff$ =
          of(true)
            .pipe(
              tap(() => {
                this.loadingContracts = true;
                this.loadingBoxes = true;
              }),
              switchMap(() => this.contractsService.getAllContractsByCompany(this.loggedUser.company.id, [this.contractStatuses.new, this.contractStatuses.testbedAssociation, this.contractStatuses.ongoing])),
              tap(contracts => {
                this.contracts = contracts;
                this.loadingContracts = false;
              }),
              tap(() => {
                if (this.contracts && this.contracts.length !== 0) {

                  // Per ogni contratto
                  this.contracts.forEach(contract => {
                    //Se ha delle box
                    if (contract.boxes) {
                      // Scorro fra esse
                      contract.boxes.forEach(box => {
                        const alreadyAssigned = this.boxes?.find(boxAlreadyInArray => boxAlreadyInArray.id === box.id);
                        // Se la box in esame non si trova già nell'array, la pusho
                        if (!alreadyAssigned)
                          this.boxes?.push(box);
                      });
                    }
                  });
                }
              }),
              tap(() => {
                this.loadingBoxes = false;
              }),
              tap(() => this.loadItems())
            );
        this.allTheStuff$.subscribe();
        break;

      case this.roles.logistic:
        this.allTheStuff$ =
          of(true)
            .pipe(
              tap(() => {
                this.loadingContracts = true;
                this.loadingBoxes = true;
              }),
              switchMap(() => this.contractsService.getAllContracts([this.contractStatuses.testbedAssociation, this.contractStatuses.boxesWaiting])),
              tap(contracts => {
                this.contracts = contracts;
                this.loadingContracts = false;
                this.loadItems();
              }),
              switchMap(() => this.boxesService.getAllBoxes([this.boxStatuses.onTheShelf, this.boxStatuses.assigned, this.boxStatuses.shipped])),
              tap(boxes => {
                this.boxes = boxes;
                this.loadingBoxes = false;
                this.loadItems();
              }),
            );
        this.allTheStuff$.subscribe();
        break;

      case this.roles.researcher:
        this.allTheStuff$ =
          of(true)
            .pipe(
              tap(() => {
                this.loadingBoxesAndTestbeds = true;
              }),
              switchMap(() => this.usersService.getResearcherBoxesAndTestbeds(this.loggedUser)),
              tap(boxesAndTestbeds => {
                this.boxesWithTestbed = boxesAndTestbeds;
                boxesAndTestbeds.forEach(boxAndTestbed => {
                  if (boxAndTestbed.box && boxAndTestbed.testbed) {
                    const alreadyAssignedTestbed = this.testbedsIds.find(testbedAlreadyInArray => testbedAlreadyInArray === boxAndTestbed.testbed as number);
                    const alreadyAssignedBox = this.boxes?.find(boxAlreadyInArray => boxAlreadyInArray.id === boxAndTestbed.box?.id);
                    if (!alreadyAssignedTestbed) {
                      this.testbedsIds.push(boxAndTestbed.testbed as number);
                    }
                    if (!alreadyAssignedBox)
                      this.boxes?.push(boxAndTestbed.box);
                    boxAndTestbed.box.currentTestbedIdentifier = boxAndTestbed.testbed.identifier;
                  }
                });
                // FIXME: This is an hotfix and probably not the best way to implement this.
                forkJoin(this.testbedsIds.map(tid => { return this.testbedsService.getTestbedById(tid) }))
                  .subscribe(data => {
                    data.forEach(testbed => {
                      const alreadyAssignedTestbed = this.testbeds.find(testbedAlreadyInArray => testbedAlreadyInArray.id === testbed?.id);
                      if (!alreadyAssignedTestbed) {
                        this.testbeds.push(testbed);
                      }
                    })
                    this.boxesWithTestbed?.forEach(boxAndTestbed => {
                      if (boxAndTestbed.box && boxAndTestbed.testbed) {
                        const testbedIdentifier = this.testbeds.find(testbed => testbed.id === boxAndTestbed.testbed as number);
                        let box = this.boxes?.find(box => box.id == boxAndTestbed.box?.id);
                        boxAndTestbed.box.currentTestbedIdentifier = boxAndTestbed.testbed.identifier;
                        boxAndTestbed.testbed = testbedIdentifier;

                        if (box) {
                          box.currentTestbedIdentifier = testbedIdentifier?.identifier;
                          this.boxes?.push(box);
                        }
                      }
                    })
                  })
              }),
              tap(() => {
                this.loadingBoxesAndTestbeds = false;
                this.loadItems();
              })
            );
        this.allTheStuff$.subscribe();

        break;
      default:
        return;
    }

  }

  private loadItems() {

    this.isThereAnyContract = this.contracts?.length !== 0;
    this.isThereAnyBox = this.boxes?.length !== 0;
    this.isThereAnyCertificate = this.certificates?.length !== 0;
    this.isThereAnyTestbed = this.testbeds?.length !== 0;

  }

  removeContract(contract: Contract): void {
    this.contracts?.splice(this.contracts.indexOf(contract), 1);
  }

  removeBox(box: Box): void {
    this.boxes?.splice(this.boxes.indexOf(box), 1);
  }


}
